// MiniBar — barra flotante (ahorro + carrito + × cerrar) de la tienda v5.
// Fuente: maquetas/tienda-v5-*.html (patrón unificado). Se oculta al llegar al footer
// y persiste el cierre manual en sessionStorage hasta recargar la pestaña.
//
// Self-contained: <style>{MINIBAR_CSS}</style> + clases .tv5-mb-* + tokens --tv5-* propios.
//
// ⚠ Fable: el IntersectionObserver del footer se monta en useEffect (NO en un <script>
// inline) → inmune al orden de marcado. Evita el bug del "footer-después-del-script"
// que en las maquetas dejaba el observer sin footer en parse-time.
// FOUC: doble capa. (1) El `useState` initializer lee sessionStorage → primer render con
// `.cerrada`. (2) PERO el div monta con la animación tv5MbPop (opacity 0→1) que pisa el
// opacity:0 de .cerrada durante 0.5s → se veía el pop y desaparecía. Por eso hay un script
// en index.html que agrega .tv5-minibar-precerrada al <html> ANTES del bundle, y la regla
// `:root.tv5-minibar-precerrada .tv5-mb { display:none }` lo oculta de una (sin animación).
//
// Sub-fase 4.6: los datos del carrito salen de useCart() (CartContext). Las props itemsCount/total/
// faltaEnvioGratis quedan como OVERRIDE opcional — solo las usa el harness de QA (TiendaV5Preview)
// para forzar estados; las páginas reales NO las pasan → reflejan el carrito real. Si el carrito está
// vacío, la barra no se renderiza (antes era mock siempre visible con "3 · $389").
//
// Props:
//   ahorro            número|string  → chip "💰 $250" (ahorro vs súper). TODO Fase 5: calcular real desde el carrito.
//   itemsCount        número         → OVERRIDE de la cantidad de ítems (default: useCart().itemsCount)
//   total             número         → OVERRIDE del subtotal (default: useCart().subtotal)
//   faltaEnvioGratis  número|null    → OVERRIDE; si >0 muestra "Faltan $X envío gratis" (default: useCart().faltaEnvioGratis)
//   checkoutHref      string         → destino del CTA "Ir a pagar" (default '/checkout' — checkout real one-shot)
//   storageKey        string         → clave sessionStorage del cierre (default 'miniBarCerrada')
//   bumpSignal        número         → al incrementar dispara un "bump" (feedback de "agregado").
//                                      Web Animations API (no toca clases → no reejecuta el pop). reduced-motion lo saltea.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../lib/CartContext';

const fmt = (n) => '$' + Number(n).toLocaleString('es-UY');

const MINIBAR_CSS = `
/* FOUC guard: si index.html marcó el cierre pre-render, ocultar sin animación (ver index.html) */
:root.tv5-minibar-precerrada .tv5-mb { display: none; }
.tv5-mb {
  --tv5-teal:#00A8B5; --tv5-navy:#1A3461; --tv5-text:#F8F6F1;
  --tv5-muted:rgba(248,246,241,0.65); --tv5-dim:rgba(248,246,241,0.45); --tv5-border:rgba(255,255,255,0.08);
  position:fixed; bottom:calc(72px + env(safe-area-inset-bottom,0px) + 12px); left:50%;
  transform:translateX(-50%); background:rgba(10,20,36,0.92);
  backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px);
  border:1px solid var(--tv5-border); border-radius:999px; padding:6px 6px 6px 12px;
  display:flex; align-items:center; gap:8px; z-index:80;
  box-shadow:0 16px 48px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3);
  max-width:calc(100% - 24px); white-space:nowrap;
  transition:transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease;
  animation:tv5MbPop 0.5s cubic-bezier(0.34,1.56,0.64,1);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;
}
@keyframes tv5MbPop { from { opacity:0; transform:translate(-50%,20px) scale(0.9); } to { opacity:1; transform:translate(-50%,0) scale(1); } }
.tv5-mb.oculto, .tv5-mb.cerrada { opacity:0; transform:translate(-50%, calc(100% + 40px)); pointer-events:none; }
.tv5-mb-ahorro { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-weight:800;
  color:#34D399; padding:4px 10px; background:rgba(16,185,129,0.14); border:1px solid rgba(16,185,129,0.35); border-radius:999px; }
.tv5-mb-carrito { display:inline-flex; flex-direction:column; gap:0; padding:0 4px; font-size:12px; color:var(--tv5-text); min-width:0; }
.tv5-mb-carrito b { font-weight:800; letter-spacing:-0.01em; }
.tv5-mb-carrito small { font-size:10px; color:var(--tv5-dim); font-weight:500; line-height:1.2; overflow:hidden; text-overflow:ellipsis; }
@media (max-width:519px){ .tv5-mb-carrito small { display:none; } }
.tv5-mb-cta { display:inline-flex; align-items:center; gap:4px; background:var(--tv5-teal); color:var(--tv5-navy);
  padding:8px 14px; border-radius:999px; font-size:12px; font-weight:800; text-decoration:none; white-space:nowrap;
  box-shadow:0 4px 12px rgba(0,168,181,0.35); min-height:44px; transition:transform 0.2s; }
.tv5-mb-close { background:rgba(255,255,255,0.08); border:1px solid var(--tv5-border); color:var(--tv5-muted);
  width:30px; height:30px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center;
  font-size:18px; font-weight:400; cursor:pointer; padding:0; line-height:1; box-sizing:border-box; flex-shrink:0;
  position:relative; transition:all 0.2s; -webkit-tap-highlight-color:transparent; }
.tv5-mb-close::after { content:''; position:absolute; inset:-7px; } /* tap 44px sin agrandar el círculo */
.tv5-mb-close:active { background:rgba(255,255,255,0.14); color:var(--tv5-text); }
@media (hover:hover){
  .tv5-mb-cta:hover { transform:translateY(-1px) scale(1.03); }
  .tv5-mb-close:hover { background:rgba(255,255,255,0.14); color:var(--tv5-text); border-color:rgba(255,255,255,0.25); transform:scale(1.05); }
}
@media (min-width:900px){
  .tv5-mb { bottom:24px; padding:8px 12px 8px 16px; gap:12px; }
  .tv5-mb-ahorro { font-size:13px; padding:6px 12px; }
  .tv5-mb-carrito { font-size:13px; }
  .tv5-mb-cta { font-size:13px; padding:10px 18px; }
  .tv5-mb-close { width:34px; height:34px; font-size:20px; }
}
@media (prefers-reduced-motion:reduce){ .tv5-mb { animation:none; transition:opacity 0.2s; } }
`;

export function MiniBar({
  ahorro,
  itemsCount: itemsCountProp,
  total: totalProp,
  faltaEnvioGratis: faltaProp,
  checkoutHref = '/checkout',
  storageKey = 'miniBarCerrada',
  bumpSignal,
}) {
  const cart = useCart();
  // Datos del carrito real; las props (si vienen) pisan — solo el harness de QA las pasa.
  const itemsCount = itemsCountProp ?? cart.itemsCount;
  const total = totalProp ?? cart.subtotal;
  const faltaEnvioGratis = faltaProp ?? cart.faltaEnvioGratis;
  const ref = useRef(null);
  // FOUC: lee el cierre persistido en el initializer → primer render ya sale oculto.
  const [cerrada, setCerrada] = useState(() => {
    try { return sessionStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const [ocultaFooter, setOcultaFooter] = useState(false);

  // Observer del footer en useEffect (inmune al orden de marcado — Fable).
  useEffect(() => {
    const footer = document.querySelector('footer');
    const bar = ref.current;
    if (!footer || !bar) return;
    const obs = new IntersectionObserver(
      ([entry]) => setOcultaFooter(entry.isIntersecting),
      { rootMargin: '0px 0px 80px 0px', threshold: 0 }
    );
    obs.observe(footer);
    return () => obs.disconnect();
  }, []);

  // Reabrir al agregar (Nico, Sub-fase 4.6): si la barra estaba cerrada manualmente (× → sessionStorage)
  // y el user vuelve a agregar algo, es señal de intención de compra → reaparece con el CTA "Ir a pagar".
  // Separado del bump WAA para que también reaparezca con prefers-reduced-motion. setCerrada(false) es
  // no-op si ya estaba abierta (React bail-out) → no afecta el caso normal.
  useEffect(() => {
    if (!bumpSignal) return; // saltea el mount inicial (undefined/0)
    setCerrada(false);
    try { sessionStorage.removeItem(storageKey); } catch { /* noop */ }
  }, [bumpSignal, storageKey]);

  // FIX (Nico 4.6): el guard anti-FOUC es una clase en <html> que index.html agrega al cargar si
  // sessionStorage marcaba cerrada, y la regla `:root.tv5-minibar-precerrada .tv5-mb{display:none}`
  // la ocultaba. Al reabrir (add), React ponía cerrada=false pero esa clase seguía → la barra quedaba
  // display:none igual. La sacamos SOLO cuando la barra debe verse (cerrada=false) para no reintroducir
  // el flash del pop cuando sí está cerrada. Cubre el mount-abierta y el reappear-al-agregar.
  useEffect(() => {
    if (!cerrada) document.documentElement.classList.remove('tv5-minibar-precerrada');
  }, [cerrada]);

  // Bump al agregar al carrito (producto-detalle). WAA en vez de clase CSS → no reejecuta el pop.
  useEffect(() => {
    if (!bumpSignal) return; // saltea el mount inicial (undefined/0)
    const bar = ref.current;
    if (!bar || typeof bar.animate !== 'function') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const anim = bar.animate(
      [
        { transform: 'translate(-50%,0) scale(1)' },
        { transform: 'translate(-50%,0) scale(1.07)', offset: 0.3 },
        { transform: 'translate(-50%,0) scale(1)' },
      ],
      { duration: 350, easing: 'ease' }
    );
    return () => anim.cancel();
  }, [bumpSignal]);

  function cerrar() {
    setCerrada(true);
    try { sessionStorage.setItem(storageKey, 'true'); } catch { /* noop */ }
  }

  // Carrito vacío → sin barra. (Los hooks corren arriba sin condicionar → orden estable.)
  if (itemsCount <= 0) return null;

  const cls = 'tv5-mb' + (cerrada ? ' cerrada' : '') + (ocultaFooter ? ' oculto' : '');
  return (
    <div className={cls} ref={ref}>
      <style>{MINIBAR_CSS}</style>
      {ahorro != null && <span className="tv5-mb-ahorro">💰 {typeof ahorro === 'number' ? fmt(ahorro) : ahorro}</span>}
      <div className="tv5-mb-carrito">
        <b>{itemsCount} · {fmt(total)}</b>
        {faltaEnvioGratis > 0 && <small>Faltan {fmt(faltaEnvioGratis)} envío gratis</small>}
      </div>
      <Link to={checkoutHref} className="tv5-mb-cta">Ir a pagar →</Link>
      <button className="tv5-mb-close" onClick={cerrar} aria-label="Cerrar barra (el carrito sigue en el nav)" title="Cerrar">×</button>
    </div>
  );
}
